import LeanLawAuth from '@/services/LeanLawAuth';

export const ACCOUNT_DISABLED = 'ACCOUNT_DISABLED';
export const SESSION_EXPIRED_MESSAGE =
  'To keep your information safe, you were logged out of LeanLaw. Click OK to log back in.';

interface Auth {
  logoff(): void;
  logoffTo(encodedRedirect: string): void;
}

interface UnauthorizedOptions {
  /** The boot check (/health/auth) sends a plain 401 to login itself. */
  isAuthCheck?: boolean;
  auth?: Auth;
}

declare global {
  interface Window {
    bootbox?: {
      alert(options: { message: string; callback: () => void }): { css(name: string, value: string): unknown } | undefined;
    };
  }
}

// Above Vuetify overlays (2000+), whose scrim would otherwise cover the notice's OK button.
export const SESSION_NOTICE_Z_INDEX = '3000';

let registeredAuth: Auth | undefined;

/** The Angular-injected leanLawAuth, so axios 401s in local dev also log off against env.json's domain. */
export function registerAuth(auth: Auth): void {
  registeredAuth = auth;
}

// Download requests (responseType blob/arraybuffer) carry the 401 body unparsed.
async function readErrorCode(data: unknown): Promise<unknown> {
  try {
    let body = data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      body = JSON.parse(await data.text());
    } else if (data instanceof ArrayBuffer) {
      body = JSON.parse(new TextDecoder().decode(data));
    }
    return (body as { error?: unknown } | null)?.error;
  } catch {
    return undefined;
  }
}

let signingOut = false;
let releaseTimer: ReturnType<typeof setTimeout> | undefined;

// A beforeunload prompt (e.g. unsaved conflict-check notes) can cancel the logoff navigation and leave
// the page running; without a release the latch would swallow every later 401 forever.
export const SIGN_OUT_RELEASE_MS = 10_000;

function signOut(navigate: () => void): void {
  navigate();
  clearTimeout(releaseTimer);
  releaseTimer = setTimeout(() => {
    signingOut = false;
  }, SIGN_OUT_RELEASE_MS);
}

/**
 * The single 401 policy for the axios and AngularJS $http interceptors. Resolves true when the
 * page is navigating away, so the caller swallows the error instead of letting its catch handlers toast.
 */
export async function handleUnauthorized(
  data: unknown,
  { isAuthCheck = false, auth = registeredAuth ?? new LeanLawAuth() }: UnauthorizedOptions = {},
): Promise<boolean> {
  if (signingOut) return true;
  const errorCode = await readErrorCode(data);
  // Concurrent 401s all pass the first check before any of them resolves its body.
  if (signingOut) return true;

  if (errorCode === ACCOUNT_DISABLED) {
    signingOut = true;
    // To the login page rather than back to the app, so the user sees why instead of booting into a fresh 401.
    signOut(() => auth.logoffTo(encodeURIComponent('/Account/Login?reason=disabled')));
    return true;
  }

  if (isAuthCheck) return false;

  signingOut = true;
  // Logoff, not login or reload: while /health/auth still says authenticated, either one 401s again forever.
  const logoff = () => signOut(() => auth.logoff());
  if (window.bootbox) {
    window.bootbox
      .alert({ message: SESSION_EXPIRED_MESSAGE, callback: logoff })
      ?.css('z-index', SESSION_NOTICE_Z_INDEX);
  } else {
    logoff();
  }
  return true;
}

export function resetForTests(): void {
  clearTimeout(releaseTimer);
  registeredAuth = undefined;
  signingOut = false;
}
